import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Button } from '@mui/material';
import { FaArrowUp } from 'react-icons/fa';
import logo from '../assets/images/logos/logo.png';
import { CONSENT_REOPEN_EVENT } from '../utils/consent';

const linkSx = {
  color: 'rgba(255,255,255,0.4)',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': { color: 'rgba(255,255,255,0.7)' },
};

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'secondary.main',
        color: '#FFFFFF',
        mt: 'auto',
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            py: { xs: 5, md: 6 },
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1.5fr 1fr 1fr 1fr',
            },
            gap: { xs: 4, md: 5 },
          }}
        >
          {/* Brand */}
          <Box>
            <Box
              component={Link}
              to="/"
              sx={{ display: 'inline-block', mb: 2 }}
            >
              <img
                src={logo}
                alt="Your Away Day"
                style={{
                  height: 56,
                  width: 'auto',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                maxWidth: 260,
                lineHeight: 1.7,
              }}
            >
              Fan-favourite pubs, restaurants, and hotels near football
              stadiums worldwide.
            </Typography>
          </Box>

          {/* Explore */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: 'rgba(255,255,255,0.4)',
                display: 'block',
                mb: 1.5,
                fontSize: '0.7rem',
              }}
            >
              Explore
            </Typography>
            {[
              { label: 'Stadiums', to: '/stadiums' },
              { label: 'World Cup 2026', to: '/world-cup-2026' },
              { label: 'My Trips', to: '/my-trips' },
              { label: 'Advertise', to: '/advertise' },
            ].map((link) => (
              <Typography
                key={link.label}
                component={Link}
                to={link.to}
                variant="body2"
                sx={{
                  display: 'block',
                  color: 'rgba(255,255,255,0.6)',
                  textDecoration: 'none',
                  py: 0.5,
                  '&:hover': { color: '#FFFFFF' },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Box>

          {/* Popular */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: 'rgba(255,255,255,0.4)',
                display: 'block',
                mb: 1.5,
                fontSize: '0.7rem',
              }}
            >
              Popular
            </Typography>
            {[
              { label: 'England', to: '/stadiums/england' },
              { label: 'Spain', to: '/stadiums/spain' },
              { label: 'Germany', to: '/stadiums/germany' },
              { label: 'Italy', to: '/stadiums/italy' },
              { label: 'USA', to: '/stadiums/united-states-of-america' },
            ].map((link) => (
              <Typography
                key={link.label}
                component={Link}
                to={link.to}
                variant="body2"
                sx={{
                  display: 'block',
                  color: 'rgba(255,255,255,0.6)',
                  textDecoration: 'none',
                  py: 0.5,
                  '&:hover': { color: '#FFFFFF' },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Box>

          {/* Business */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: 'rgba(255,255,255,0.4)',
                display: 'block',
                mb: 1.5,
                fontSize: '0.7rem',
              }}
            >
              For Businesses
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                mb: 2,
                lineHeight: 1.6,
              }}
            >
              Own a venue near a stadium? Get featured in our matchday
              guides.
            </Typography>
            <Button
              component={Link}
              to="/advertise"
              variant="outlined"
              size="small"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                borderColor: 'rgba(255,255,255,0.2)',
                fontSize: '0.8125rem',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.05)',
                },
              }}
            >
              Learn more
            </Button>
          </Box>
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            py: 3,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.35)' }}
            >
              &copy; {new Date().getFullYear()} Trueight Ltd
            </Typography>
            <Typography component={Link} to="/affiliate-disclosure" variant="caption" sx={linkSx}>
              Affiliate disclosure
            </Typography>
            <Typography component={Link} to="/privacy" variant="caption" sx={linkSx}>
              Privacy
            </Typography>
            <Typography component={Link} to="/cookies" variant="caption" sx={linkSx}>
              Cookies
            </Typography>
            <Typography
              component="button"
              type="button"
              variant="caption"
              onClick={() => window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT))}
              sx={{
                ...linkSx,
                background: 'none',
                border: 'none',
                p: 0,
                fontFamily: 'inherit',
              }}
            >
              Cookie settings
            </Typography>
          </Box>
          <Button
            onClick={scrollToTop}
            size="small"
            startIcon={<FaArrowUp size={10} />}
            sx={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: '0.75rem',
              fontWeight: 400,
              '&:hover': { color: 'rgba(255,255,255,0.7)' },
            }}
          >
            Back to top
          </Button>
        </Box>

        {/* Independent-guide disclaimer. Reduces implied-endorsement risk on
            our nominative use of "FIFA World Cup" — we are not an official partner. */}
        <Box sx={{ pb: 3 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.25)',
              fontSize: '0.6875rem',
              lineHeight: 1.6,
            }}
          >
            An independent matchday guide, not affiliated with, endorsed by, or
            sponsored by FIFA. FIFA, World Cup, and FIFA World Cup are trademarks of FIFA.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
